// The project's measurement system, read off Giraffe.
//
// It lives on `project.properties.units` as `{length, area}` — e.g.
// `{length:"ft", area:"sqft"}` — on the REACTIVE `project` state key, not on
// `fetchProjectDetails`, which does not carry it. The SDK types do not declare
// it and older projects spell it as a bare "metric"/"imperial" string, so it is
// read defensively and answers null rather than guessing.
//
// What it decides is DISPLAY only: the project's numbers are metric either way
// (see data/units.ts), and this is the setting that says which units the sheet
// speaks. The workbook's own override beats it — same shape as
// `~/Documents/code/roads`, where this pattern came from.

import type { UnitSystem } from '@/data/units';

export interface ProjectUnits {
  length?: string;
  area?: string;
}

const clean = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;

/** Pull the setting off the `project` feature. Null = not set, or unreadable. */
export function projectUnits(project: unknown): ProjectUnits | null {
  if (!project || typeof project !== 'object') return null;
  const props = ((project as { properties?: unknown }).properties ?? {}) as Record<string, unknown>;
  const raw = props.units ?? props.unit ?? props.unitSystem;

  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const length = clean(o.length ?? o.distance);
    const area = clean(o.area);
    return length !== undefined || area !== undefined ? { length, area } : null;
  }

  // Some projects carry one word instead of a pair.
  const word = clean(raw)?.toLowerCase();
  if (word === 'imperial' || word === 'us') return { length: 'ft', area: 'sqft' };
  if (word === 'metric' || word === 'si') return { length: 'm', area: 'sqm' };
  return null;
}

const IMPERIAL = /^(ft|f|feet|foot|sqft|sf|ft2|yd|yds|yard|yards|sqyd|ac|acre|acres|mi|mile|miles|in|inch|inches)$/;
const METRIC = /^(m|metre|metres|meter|meters|sqm|m2|km|km2|ha|hectare|hectares|cm|mm)$/;

/**
 * Classify the pair. Null when the project said nothing this recognises — the
 * caller decides what to do with that, and for this app the answer is metric,
 * because metric is what Giraffe's numbers already are.
 */
export function unitSystem(units: ProjectUnits | null): UnitSystem | null {
  if (!units) return null;
  const tokens = [units.length, units.area]
    .filter((v): v is string => v !== undefined)
    .map((v) => v.toLowerCase().replace(/[²³^.\s]/g, ''));
  if (tokens.some((t) => IMPERIAL.test(t))) return 'imperial';
  if (tokens.some((t) => METRIC.test(t))) return 'metric';
  return null;
}

/** "ft / sqft" — so an unrecognised spelling is still visible in the tooltip. */
export function unitsLabel(units: ProjectUnits | null): string {
  if (!units) return 'not set';
  return [units.length, units.area].filter(Boolean).join(' / ') || 'not set';
}

/**
 * The system in force: the workbook's override, else the project's setting,
 * else metric.
 *
 * Metric last on purpose. This app SHOWS the project's numbers, and they are
 * metric — so a project that says nothing is left exactly as it reads today,
 * rather than being flipped into feet on a guess. (roads defaults the other way
 * because it DRAWS in feet; the difference is deliberate.)
 */
export function resolveSystem(override: UnitSystem | undefined, project: ProjectUnits | null): UnitSystem {
  return override ?? unitSystem(project) ?? 'metric';
}
